import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Footer, Header } from "../../components";
import { STUDENT_PORTAL_URL } from "../../constants/Matcher";
import { get_events } from "../../apis/Dashboard";
import { C, FONT } from "../../constants/mq";

// Where a student who already belongs comes back to.
//
// Everything on this page is behind one login — our own portal — so each link
// carries a `next` and the portal drops you on the page you clicked rather
// than its dashboard. That is also why there is no password field here: a
// second place to sign in would mean this site handling credentials and then
// handing a token to the portal through the URL, where it lands in browser
// history and server logs. One sign-in, one place that knows how to do it.
//
// "What's on" only exists when something is published. An empty events
// heading tells a student nothing except that we have nothing on.

const portal = (next) =>
  `${STUDENT_PORTAL_URL}/login?next=${encodeURIComponent(next)}`;

// `to: null` means we have nowhere to send them yet — listed, but not
// pretending to be a link.
const LINKS = [
  { label: "Student portal", to: portal("/dashboard"), external: true,
    note: "Lectures, homework and results" },
  { label: "Timetables", to: portal("/calendar"), external: true,
    note: "Your classes, week by week" },
  { label: "Notices", to: portal("/notification"), external: true,
    note: "From your campus" },
  { label: "Answer booklet", to: portal("/answer-booklet"), external: true,
    note: "Worked answers for your year" },
  { label: "Handbooks", to: null, note: "Coming soon" },
  { label: "Ask a question", to: "/resources", external: false,
    note: "Guides, past papers and study notes" },
];

const CONTACT = [
  {
    title: "Call us",
    lines: ["02 8883 2555", "Mon, Tue, Wed, Fri: 12pm – 7pm", "Sat: 9am – 5pm"],
    href: "tel:0288832555",
  },
  {
    title: "In person",
    lines: ["Call us or visit", "121/14 Lexington Drive", "Bella Vista NSW 2153"],
  },
  {
    title: "Find an answer",
    lines: ["Guides, past papers and study notes"],
    href: "/resources",
  },
  {
    title: "Online enquiries",
    lines: ["Send us a message and we will come back to you"],
    href: "/contact-us",
  },
];

// Banner matches CommonBanner (the only red on the page); everything below
// sits on white or sand, with white cards and hairline rules.
const s = {
  page: { background: "#fff", minHeight: "100vh", fontFamily: FONT },
  hero: {
    background: `linear-gradient(112deg, ${C.bright} 0%, ${C.bright} 66%, #C2001A 66%, #C2001A 100%)`,
    color: "#fff",
    padding: "58px 0 54px",
  },
  container: { maxWidth: 1072, margin: "0 auto", padding: "0 24px" },
  eyebrow: {
    display: "block", fontSize: 12.5, color: "rgba(255,255,255,0.85)",
    margin: "0 0 14px",
  },
  h1: {
    fontSize: 40, fontWeight: 800, letterSpacing: "-0.5px", margin: 0,
    lineHeight: 1.18, color: "#fff", maxWidth: 760,
  },
  lead: {
    fontSize: 15.5, lineHeight: 1.65, color: "rgba(255,255,255,0.92)",
    margin: "14px 0 0", maxWidth: 620,
  },

  body: { padding: "56px 0 28px" },
  split: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
    gap: 40, alignItems: "start",
  },
  linksCol: { minWidth: 0 },
  h2: {
    fontSize: 26, fontWeight: 700, color: C.ink, margin: "0 0 16px",
    letterSpacing: "-0.3px",
  },

  linkRow: {
    alignItems: "center", gap: 16, padding: "15px 2px",
    borderBottom: `1px solid ${C.line}`,
  },
  linkRowStatic: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 16, padding: "15px 2px", borderBottom: `1px solid ${C.line}`,
    color: C.ink,
  },
  linkName: { fontSize: 16, fontWeight: 600, margin: 0 },
  linkNote: { fontSize: 13, fontWeight: 400, color: C.muted, margin: "3px 0 0" },
  soon: {
    fontSize: 11, fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase",
    color: C.muted, flexShrink: 0,
  },

  signin: { padding: "26px 26px 28px" },
  signinTitle: { fontSize: 19, fontWeight: 700, color: C.ink, margin: "0 0 8px" },
  signinBody: { fontSize: 14, color: C.body, margin: "0 0 20px", lineHeight: 1.65 },
  signinBtn: { display: "flex", width: "100%" },
  signinAlt: {
    display: "block", textAlign: "center", marginTop: 12, fontSize: 13.5,
    color: C.red, fontWeight: 500, textDecoration: "none",
  },
  signinRule: { border: "none", borderTop: `1px solid ${C.line}`, margin: "22px 0 18px", opacity: 1 },
  signinFoot: { fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.6 },
  signinLink: { color: C.red, fontWeight: 600, textDecoration: "none" },

  week: { padding: "20px 0 8px" },
  cards: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 20,
  },
  card: { padding: "22px 22px 24px" },
  cardFirst: { borderTop: `4px solid ${C.bright}` },
  cardTitle: { fontSize: 17, fontWeight: 700, color: C.ink, margin: "0 0 8px", lineHeight: 1.3 },
  cardBody: { fontSize: 13.5, lineHeight: 1.6, margin: 0, color: C.body },
  cardMeta: { fontSize: 12.5, margin: "12px 0 0", color: C.muted },

  contact: {
    background: C.sand, borderTop: `1px solid ${C.line}`, padding: "56px 0 64px",
    marginTop: 32,
  },
  contactIntro: { fontSize: 15, color: C.body, margin: "0 0 28px" },
  contactGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 28,
  },
  contactTitle: { fontSize: 17, fontWeight: 700, color: C.ink, margin: "0 0 10px" },
  contactLink: { fontSize: 17, fontWeight: 700, margin: "0 0 10px" },
  contactLine: { fontSize: 14, color: C.body, margin: "0 0 3px", lineHeight: 1.6 },
};

const CurrentStudents = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    get_events()
      .then((r) => {
        if (Array.isArray(r?.data)) setEvents(r.data.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  const Row = ({ item }) => {
    const inner = (
      <>
        <span>
          <p style={s.linkName}>{item.label}</p>
          <p style={s.linkNote}>{item.note}</p>
        </span>
        {item.to ? null : <span style={s.soon}>Soon</span>}
      </>
    );
    if (!item.to) return <div style={s.linkRowStatic}>{inner}</div>;
    if (item.external) {
      return (
        <a className="mq-row" style={s.linkRow} href={item.to} target="_blank" rel="noopener noreferrer">
          {inner}
        </a>
      );
    }
    return <Link className="mq-row" style={s.linkRow} to={item.to}>{inner}</Link>;
  };

  return (
    <div style={s.page}>
      <Header />
      <main>
        <section style={s.hero}>
          <div style={s.container}>
            <span style={s.eyebrow}>Current students</span>
            <h1 style={s.h1}>Everything for the term you are in</h1>
            <p style={s.lead}>
              Your lectures, homework, class times and results all live in the
              student portal. Sign in once and the links below take you straight
              to the page you picked.
            </p>
          </div>
        </section>

        <section style={s.body}>
          <div style={s.container}>
            <div style={s.split}>
              <div style={s.linksCol}>
                <h2 style={s.h2}>Popular links</h2>
                {LINKS.map((l) => (
                  <Row key={l.label} item={l} />
                ))}
              </div>

              <div className="mq-sand" style={s.signin}>
                <p style={s.signinTitle}>Sign in</p>
                <p style={s.signinBody}>
                  One account covers every link on this page.
                </p>
                <a
                  className="mq-btn"
                  style={s.signinBtn}
                  href={portal("/dashboard")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Go to the student portal
                </a>
                <a
                  style={s.signinAlt}
                  href={`${STUDENT_PORTAL_URL}/forgot-password`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Forgotten your password?
                </a>
                <hr style={s.signinRule} />
                <p style={s.signinFoot}>
                  Not with us yet?{" "}
                  <Link to="/enroll" style={s.signinLink}>Enrol now</Link> or{" "}
                  <Link to="/our-courses" style={s.signinLink}>browse the courses</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {events.length > 0 && (
          <section style={s.week}>
            <div style={s.container}>
              <h2 style={s.h2}>What&rsquo;s on</h2>
              <div style={s.cards}>
                {events.map((e, i) => (
                  <div
                    key={e.id || i}
                    className="mq-card mq-card--hover"
                    style={{ ...s.card, ...(i === 0 ? s.cardFirst : null) }}
                  >
                    <p style={s.cardTitle}>{e.title}</p>
                    {e.description && (
                      <p style={s.cardBody}>
                        {e.description}
                      </p>
                    )}
                    {e.date && (
                      <p style={s.cardMeta}>
                        {new Date(e.date).toLocaleDateString("en-AU", {
                          weekday: "short",
                          day: "numeric",
                          month: "long",
                        })}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section style={s.contact}>
          <div style={s.container}>
            <h2 style={s.h2}>Contact us</h2>
            <p style={s.contactIntro}>
              If you are already studying with us, this is how to get in touch.
            </p>
            <div style={s.contactGrid}>
              {CONTACT.map((c) => (
                <div key={c.title}>
                  {!c.href ? (
                    <p style={s.contactTitle}>{c.title}</p>
                  ) : c.href.startsWith("/") ? (
                    <Link to={c.href} className="mq-link" style={s.contactLink}>
                      {c.title}
                    </Link>
                  ) : (
                    <a href={c.href} className="mq-link" style={s.contactLink}>
                      {c.title}
                    </a>
                  )}
                  {c.lines.map((l) => (
                    <p key={l} style={s.contactLine}>{l}</p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CurrentStudents;
